
import React from "react";
import { useFacilities } from "@/hooks/useFacilities";
import { useProviderGallery } from "@/hooks/useProviderGallery";

interface ProviderImagesFromFacilitiesProps {
  providerId: string;
  fallbackImages: string[];
  children: (images: string[]) => React.ReactNode;
}

/**
 * Fetches all images for facilities and gallery of a given provider.
 * If no facility or gallery images are present, falls back to provided fallbackImages (e.g., logo or empty).
 */
const ProviderImagesFromFacilities: React.FC<ProviderImagesFromFacilitiesProps> = ({
  providerId,
  fallbackImages,
  children,
}) => {
  const { data: facilities, isLoading: facilitiesLoading, error: facilitiesError } = useFacilities(providerId);
  const { data: galleryImages, isLoading: galleryLoading, error: galleryError } = useProviderGallery(providerId);

  // Collect all images from facilities (only valid, non-empty strings)
  const facilityImages = (
    facilities?.map((facility) => facility.image_url).filter(Boolean) as string[]
  ) || [];

  // Collect all images from gallery (only valid, non-empty strings)
  const galleryImageUrls = (
    galleryImages?.map((image) => image.image_url).filter(Boolean) as string[]
  ) || [];

  // Combine facility and gallery images, with gallery images first
  const allImages = [...galleryImageUrls, ...facilityImages];

  // Use combined images if at least one exists, else use fallback images
  const images = allImages.length > 0 ? allImages : fallbackImages;

  return <>{children(images)}</>;
};

export default ProviderImagesFromFacilities;
